import React, { useEffect, useMemo, useState } from 'react'
import { Drawer } from '../../../../shared/drawer/drawer'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { deleteOrgRetention, putOrgRetention } from '../../../../shared/audit/audit-api'
import { OrgPicker } from '../../../../shared/org-search/org-picker'
import { organizationNameOf } from '../../../../shared/org-search/org-search-api'
import { HOLD_COPY, ORG_PERIODS, boundsLine, boundsProblem, orgName, rangeOf, retentionError } from './retention-format'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'

const blank = v => v === '' || v === null || v === undefined
const asText = v => (blank(v) ? '' : String(v))

const SAVE_FAILED = "The override couldn't be saved. Please try again."
const REMOVE_FAILED = "The override couldn't be removed. Please try again."
const NO_SUCH_ID = 'No organization has that ID.'
const LIFT_LABEL = 'Why is the hold being lifted?'

// One organization's retention override (phase 5 Task 9): its own periods
// (blank = the platform default, shown beside each), a legal hold with a
// reason, and Remove override.
//
// Save sends only what changed (content merges the body over the stored
// row: an omitted field is unchanged). A period emptied on an existing
// override goes in "clear" -- content's way back to the platform default;
// null would mean "unchanged". Placing a hold needs a reason; lifting one
// needs its own new reason. Removing a held override needs that reason too
// (DELETE ?reason=). Every refusal shows the server's coded sentence.
//
// override: the row ({organizationID, ...}) or null to add one; then the
// organization is picked with the searchable picker (OrgPicker, which
// searches content; `organizations` only names the ones the page knows),
// minus `taken` (ids that
// already have an override) -- or typed as an ID when the names couldn't be
// loaded (orgsFailed). Values outside `bounds` are caught inline. readOnly
// (an impersonated session) disables Save and Remove. A refusal that means
// the page is out of date (a change made elsewhere) goes to onStale(message)
// so the page can reload and say what happened.
export const OverrideDrawer = ({ open, override, organizations = [], taken = [], orgsFailed = false, platform, bounds, readOnly = false, onClose, onSaved, onStale }) => {
  const adding = !override
  const start = useMemo(() => {
    const v = { orgID: override ? String(override.organizationID) : '', legalHold: Boolean(override && override.legalHold), reason: (override && override.reason) || '' }
    ORG_PERIODS.forEach(p => {
      v[p.key] = asText(override && override[p.key])
    })
    return v
  }, [override])

  const [form, setForm] = useState(start)
  const [liftReason, setLiftReason] = useState('')
  const [removeReason, setRemoveReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const [confirming, setConfirming] = useState(false)
  const [removeError, removeFailure, setRemoveError] = useErrorLine()

  useEffect(() => {
    setForm(start)
    setLiftReason('')
    setRemoveReason('')
    setError(null)
    setConfirming(false)
    setRemoveError(null)
  }, [start, open, setError, setRemoveError])

  // The names the page knows, plus the one just picked (the picker found it).
  const names = useMemo(() => {
    const m = new Map(organizations.map(o => [Number(o.id), o.name]))
    const picked = form.orgID && organizationNameOf(form.orgID)
    if (picked) m.set(Number(form.orgID), picked)
    return m
  }, [organizations, form.orgID])

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }))

  const wasHeld = start.legalHold
  const placing = form.legalHold && !wasHeld
  const lifting = wasHeld && !form.legalHold

  // The body: only what changed.
  const body = {}
  const clear = []
  ORG_PERIODS.forEach(p => {
    if (form[p.key] === start[p.key]) return
    if (blank(form[p.key])) {
      if (!adding) clear.push(p.key)
    } else body[p.key] = Number(form[p.key])
  })
  if (clear.length) body.clear = clear
  if (placing) {
    body.legalHold = true
    body.reason = form.reason.trim()
  } else if (lifting) {
    body.legalHold = false
    body.reason = liftReason.trim()
  } else if (form.legalHold && form.reason.trim() !== start.reason.trim()) body.reason = form.reason.trim()

  const problems = {}
  ORG_PERIODS.forEach(p => {
    const problem = boundsProblem(p.key, form[p.key], bounds)
    if (problem) problems[p.key] = problem
  })
  const invalid = Object.keys(problems).length > 0
  const typedID = adding && orgsFailed
  const pickedOrg = adding ? (typedID ? Number.isInteger(Number(form.orgID)) && Number(form.orgID) > 0 : Boolean(form.orgID)) : true

  const anyPeriod = ORG_PERIODS.some(p => !blank(form[p.key]))
  const changed = Object.keys(body).length > 0
  // An unheld override with no period of its own is no override (content:
  // retention.empty_override); lifting a hold-only override is fine (content
  // removes it).
  const empty = !form.legalHold && !anyPeriod && !lifting
  const reasonMissing = (placing && !form.reason.trim()) || (lifting && !liftReason.trim()) || (form.legalHold && !placing && !form.reason.trim())
  const canSave = !readOnly && !busy && changed && !empty && !invalid && !reasonMissing && pickedOrg

  const orgID = adding ? Number(form.orgID) : override.organizationID
  const title = adding && !pickedOrg ? 'New retention override' : `Retention for ${orgName(names, orgID)}`

  // A refusal: plain copy; a stale one goes back to the page to reload.
  // A typed ID that matches no organization is a typo, not a change made
  // elsewhere: keep the drawer and what was typed.
  const refused = (err, fallback, show) => {
    if (typedID && err && err.data && err.data.code === 'retention.unknown_organization') {
      show(NO_SUCH_ID, err)
      return
    }
    const { message, stale } = retentionError(err, { bounds, sent: body, fallback })
    if (stale && onStale) onStale(message, err)
    else show(message, err)
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      await putOrgRetention(orgID, body)
      onSaved && onSaved()
    } catch (err) {
      refused(err, SAVE_FAILED, setError)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    setRemoveError(null)
    try {
      await deleteOrgRetention(orgID, wasHeld ? removeReason.trim() : undefined)
      setConfirming(false)
      onSaved && onSaved()
    } catch (err) {
      refused(err, REMOVE_FAILED, setRemoveError)
    } finally {
      setBusy(false)
    }
  }

  const id = suffix => `rt-ovr-${suffix}`

  return (
    <Drawer open={open} title={title} onClose={onClose}>
      <form className="d-grid gap-3" onSubmit={e => { e.preventDefault(); if (canSave) save() }}>
        {typedID &&
          <div className="sw-field">
            <label className="sw-label" htmlFor={id('org')}>Organization ID</label>
            <input id={id('org')} type="number" inputMode="numeric" min={1} step={1} className="form-control" value={form.orgID} onChange={e => set('orgID', e.target.value)} />
            <span className="sw-hint">The organization's number, as shown in its address in the admin.</span>
          </div>}
        {adding && !typedID &&
          <div className="sw-field">
            <label className="sw-label" htmlFor={id('org')}>Organization</label>
            <OrgPicker id={id('org')} label="Organization" exclude={taken} value={form.orgID} onChange={v => set('orgID', v)} />
          </div>}

        <p className="sw-hint m-0">Leave a period blank to use the platform default. {bounds && boundsLine(bounds)}</p>

        {ORG_PERIODS.map(p =>
          <div className="sw-field" key={p.key}>
            <label className="sw-label" htmlFor={id(p.key)}>{p.label}</label>
            <input
              id={id(p.key)}
              type="number"
              inputMode="numeric"
              min={bounds ? rangeOf(p.key, bounds).min : 1}
              max={bounds ? rangeOf(p.key, bounds).max : undefined}
              step={1}
              className={`form-control${problems[p.key] ? ' is-invalid' : ''}`}
              placeholder="Use the platform default"
              aria-describedby={id(`${p.key}-hint`)}
              aria-invalid={problems[p.key] ? true : undefined}
              value={form[p.key]}
              onChange={e => set(p.key, e.target.value)}
            />
            {problems[p.key] && <span className="sw-required" role="alert">{problems[p.key]}</span>}
            {platform && <span className="sw-hint" id={id(`${p.key}-hint`)}>Platform default: {p.unit(platform[p.key])}</span>}
          </div>)}

        <div className="sw-field">
          <div className="form-check">
            <input id={id('hold')} type="checkbox" className="form-check-input" checked={form.legalHold} onChange={e => set('legalHold', e.target.checked)} />
            <label className="form-check-label fw-semibold" htmlFor={id('hold')}>Legal hold</label>
          </div>
          <span className="sw-hint">{HOLD_COPY}</span>
        </div>

        {form.legalHold &&
          <div className="sw-field">
            <label className="sw-label" htmlFor={id('reason')}>Reason</label>
            <textarea id={id('reason')} className="form-control" rows={2} value={form.reason} onChange={e => set('reason', e.target.value)} />
            {!form.reason.trim() && <span className="sw-required">A reason is required for a legal hold.</span>}
          </div>}

        {lifting &&
          <div className="sw-field">
            <label className="sw-label" htmlFor={id('lift')}>{LIFT_LABEL}</label>
            <textarea id={id('lift')} className="form-control" rows={2} value={liftReason} onChange={e => setLiftReason(e.target.value)} />
            <span className="sw-hint">
              {anyPeriod ? 'Recorded with the change. The hold\'s own reason is kept in the history.' : 'This organization has no periods of its own, so lifting the hold removes the override.'}
            </span>
          </div>}

        {empty && changed && <p className="sw-required m-0">Set at least one period or a legal hold. To use the platform defaults for everything, remove the override.</p>}
        {error && <p className="text-danger small m-0" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} className="mt-0" />}

        <div className="d-flex flex-wrap gap-2">
          <button type="submit" className="btn sw-btn-primary" disabled={!canSave}>Save</button>
          <button type="button" className="btn sw-btn" onClick={onClose}>Cancel</button>
        </div>
      </form>

      {!adding &&
        <div className="d-grid gap-2 mt-4 pt-3 border-top">
          {wasHeld &&
            <div className="sw-field">
              <label className="sw-label" htmlFor={id('remove-reason')}>{`${LIFT_LABEL} (needed to remove this override)`}</label>
              <textarea id={id('remove-reason')} className="form-control" rows={2} value={removeReason} onChange={e => setRemoveReason(e.target.value)} />
            </div>}
          <div>
            <button type="button" className="btn sw-btn-danger" disabled={readOnly || busy || (wasHeld && !removeReason.trim())} onClick={() => { setRemoveError(null); setConfirming(true) }}>
              Remove override
            </button>
          </div>
        </div>}

      <ConfirmDialog
        open={confirming}
        title="Remove this override?"
        body={`${orgName(names, orgID)} will follow the platform defaults for every period.${wasHeld ? ' This also lifts its legal hold, so entries past those periods can be deleted at the next run.' : ''}`}
        confirmText="Remove"
        busy={busy}
        error={removeError}
        failure={removeFailure}
        onConfirm={remove}
        onCancel={() => setConfirming(false)}
      />
    </Drawer>
  )
}
